import React, { useEffect, useMemo, useState } from "react";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import { Download, TrendingUp, TrendingDown, Users, CheckCircle2, Percent, Wallet, Calculator } from "lucide-react";

const fmtIDR = (n) => "Rp " + Math.round(Number(n) || 0).toLocaleString("id-ID");

function Delta({ cur, prev, money }) {
  const up = cur >= prev;
  const diff = cur - prev;
  const pct = prev ? Math.round((diff / prev) * 100) : (cur ? 100 : 0);
  const Icon = up ? TrendingUp : TrendingDown;
  return (
    <span className={`inline-flex items-center gap-1 text-xs font-medium ${up ? "text-emerald-600" : "text-rose-600"}`}>
      <Icon className="h-3.5 w-3.5" />
      {money ? fmtIDR(Math.abs(diff)) : Math.abs(diff)} ({pct}%)
    </span>
  );
}

const STAT_META = [
  { key: "reservations", label: "Total Reservasi", icon: Users, color: "text-emerald-700" },
  { key: "closing", label: "Total Closing", icon: CheckCircle2, color: "text-emerald-700" },
  { key: "conversion", label: "Conversion Rate", icon: Percent, color: "text-amber-600", suffix: "%" },
  { key: "value", label: "Total Nilai Transaksi", icon: Wallet, color: "text-amber-600", money: true },
  { key: "avg", label: "Rata-rata / Transaksi", icon: Calculator, color: "text-emerald-700", money: true },
];

export default function SalesReview() {
  const [branches, setBranches] = useState([]);
  const [filters, setFilters] = useState({ date_from: "", date_to: "", branch_id: "all" });
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => { api.get("/branches").then((r) => setBranches(r.data)).catch(() => {}); }, []);

  const params = useMemo(() => {
    const p = {};
    if (filters.date_from) p.date_from = filters.date_from;
    if (filters.date_to) p.date_to = filters.date_to;
    if (filters.branch_id && filters.branch_id !== "all") p.branch_id = filters.branch_id;
    return p;
  }, [filters]);

  const load = () => {
    setLoading(true);
    api.get("/reports/crm-sales-review", { params })
      .then((r) => setData(r.data))
      .catch(() => toast.error("Gagal memuat Sales Review"))
      .finally(() => setLoading(false));
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [params]);

  const preset = (type) => {
    const now = new Date();
    const iso = (d) => d.toISOString().slice(0, 10);
    if (type === "this_month") setFilters((f) => ({ ...f, date_from: iso(new Date(now.getFullYear(), now.getMonth(), 1)), date_to: iso(now) }));
    else if (type === "last_month") setFilters((f) => ({ ...f, date_from: iso(new Date(now.getFullYear(), now.getMonth() - 1, 1)), date_to: iso(new Date(now.getFullYear(), now.getMonth(), 0)) }));
    else if (type === "7d") setFilters((f) => ({ ...f, date_from: iso(new Date(now.getTime() - 6 * 86400000)), date_to: iso(now) }));
  };

  const doExport = async () => {
    try {
      const res = await api.get("/reports/crm-sales-review", { params: { ...params, export: "xlsx" }, responseType: "blob" });
      const url = URL.createObjectURL(new Blob([res.data]));
      const a = document.createElement("a");
      a.href = url; a.download = "Sales_Review_CRM.xlsx"; a.click();
      URL.revokeObjectURL(url);
    } catch { toast.error("Gagal export"); }
  };

  const s = data?.summary || {};
  const cmp = data?.comparison || { this_month: {}, last_month: {} };

  return (
    <div className="space-y-6" data-testid="sales-review-page">
      <div className="flex flex-col md:flex-row md:items-end justify-between gap-4">
        <div>
          <h1 className="font-heading text-3xl tracking-tight flex items-center gap-2">
            <TrendingUp className="h-7 w-7 text-emerald-600" /> Sales Review
          </h1>
          <p className="mt-1 text-sm text-muted-foreground">
            Performa penjualan dari booking sumber CRM. Nilai transaksi diambil dari pembayaran kasir yang sudah terbayar.
          </p>
        </div>
        <Button data-testid="sales-review-export" onClick={doExport} className="bg-emerald-700 hover:bg-emerald-800">
          <Download className="mr-1.5 h-4 w-4" /> Export Excel
        </Button>
      </div>

      {/* Filters */}
      <Card>
        <CardContent className="flex flex-wrap items-end gap-3 pt-6">
          <div>
            <Label className="text-xs">Dari</Label>
            <Input type="date" data-testid="sr-date-from" className="w-[160px]" value={filters.date_from} onChange={(e) => setFilters({ ...filters, date_from: e.target.value })} />
          </div>
          <div>
            <Label className="text-xs">Sampai</Label>
            <Input type="date" data-testid="sr-date-to" className="w-[160px]" value={filters.date_to} onChange={(e) => setFilters({ ...filters, date_to: e.target.value })} />
          </div>
          <div>
            <Label className="text-xs">Cabang</Label>
            <Select value={filters.branch_id} onValueChange={(v) => setFilters({ ...filters, branch_id: v })}>
              <SelectTrigger data-testid="sr-branch" className="w-[200px]"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Semua Cabang</SelectItem>
                {branches.map((b) => (<SelectItem key={b.id} value={b.id}>{b.name}</SelectItem>))}
              </SelectContent>
            </Select>
          </div>
          <div className="flex gap-1.5">
            <Button variant="outline" size="sm" onClick={() => preset("this_month")}>Bulan Ini</Button>
            <Button variant="outline" size="sm" onClick={() => preset("last_month")}>Bulan Lalu</Button>
            <Button variant="outline" size="sm" onClick={() => preset("7d")}>7 Hari</Button>
          </div>
        </CardContent>
      </Card>

      {/* A. Summary cards */}
      <div className="grid grid-cols-2 lg:grid-cols-5 gap-3" data-testid="sr-summary">
        {STAT_META.map((m) => {
          const val = s[m.key] ?? 0;
          return (
            <Card key={m.key} className="border-t-4 border-t-emerald-500">
              <CardContent className="pt-5">
                <div className="flex items-center justify-between">
                  <span className="text-xs uppercase tracking-widest text-muted-foreground">{m.label}</span>
                  <m.icon className={`h-4 w-4 ${m.color}`} />
                </div>
                <div className={`mt-2 font-heading text-2xl ${m.color}`} data-testid={`sr-stat-${m.key}`}>
                  {m.money ? fmtIDR(val) : `${val}${m.suffix || ""}`}
                </div>
              </CardContent>
            </Card>
          );
        })}
      </div>

      {/* B. Period comparison */}
      <Card data-testid="sr-comparison">
        <CardHeader>
          <CardTitle className="font-heading text-lg">Perbandingan Bulan Ini vs Bulan Lalu</CardTitle>
        </CardHeader>
        <CardContent className="overflow-x-auto">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Metrik</TableHead>
                <TableHead className="text-right">Bulan Ini</TableHead>
                <TableHead className="text-right">Bulan Lalu</TableHead>
                <TableHead className="text-right">Perubahan</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {STAT_META.map((m) => {
                const cur = cmp.this_month?.[m.key] ?? 0;
                const prev = cmp.last_month?.[m.key] ?? 0;
                return (
                  <TableRow key={m.key}>
                    <TableCell className="font-medium text-sm">{m.label}</TableCell>
                    <TableCell className="text-right">{m.money ? fmtIDR(cur) : `${cur}${m.suffix || ""}`}</TableCell>
                    <TableCell className="text-right text-muted-foreground">{m.money ? fmtIDR(prev) : `${prev}${m.suffix || ""}`}</TableCell>
                    <TableCell className="text-right"><Delta cur={cur} prev={prev} money={m.money} /></TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </CardContent>
      </Card>

      {/* C. Top 5 treatments */}
      <Card data-testid="sr-top-treatments">
        <CardHeader><CardTitle className="font-heading text-lg">Top 5 Treatment (Booking CRM)</CardTitle></CardHeader>
        <CardContent className="overflow-x-auto">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>#</TableHead>
                <TableHead>Treatment</TableHead>
                <TableHead className="text-right">Jumlah Booking</TableHead>
                <TableHead className="text-right">Total Nilai Transaksi</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {(data?.top_treatments || []).map((t, i) => (
                <TableRow key={i}>
                  <TableCell className="text-sm">{i + 1}</TableCell>
                  <TableCell className="font-medium text-sm">{t.treatment}</TableCell>
                  <TableCell className="text-right">{t.count}</TableCell>
                  <TableCell className="text-right">{fmtIDR(t.value)}</TableCell>
                </TableRow>
              ))}
              {(!data?.top_treatments || data.top_treatments.length === 0) && (
                <TableRow><TableCell colSpan={4} className="text-center text-sm text-muted-foreground py-8">Belum ada data.</TableCell></TableRow>
              )}
            </TableBody>
          </Table>
        </CardContent>
      </Card>

      {/* D. Per-branch breakdown */}
      <Card data-testid="sr-by-branch">
        <CardHeader><CardTitle className="font-heading text-lg">Breakdown per Cabang</CardTitle></CardHeader>
        <CardContent className="overflow-x-auto">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Cabang</TableHead>
                <TableHead className="text-right">Reservasi</TableHead>
                <TableHead className="text-right">Closing</TableHead>
                <TableHead className="text-right">Conversion %</TableHead>
                <TableHead className="text-right">Nilai Transaksi</TableHead>
                <TableHead className="text-right">Rata-rata / Tx</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {(data?.by_branch || []).map((r) => (
                <TableRow key={r.branch_id}>
                  <TableCell className="font-medium text-sm">{r.branch}</TableCell>
                  <TableCell className="text-right">{r.reservations}</TableCell>
                  <TableCell className="text-right">{r.closing}</TableCell>
                  <TableCell className="text-right">{r.conversion}%</TableCell>
                  <TableCell className="text-right">{fmtIDR(r.value)}</TableCell>
                  <TableCell className="text-right">{fmtIDR(r.avg)}</TableCell>
                </TableRow>
              ))}
              {(data?.by_branch || []).length > 0 && (
                <TableRow className="bg-amber-50 font-semibold" data-testid="sr-branch-total">
                  <TableCell>TOTAL</TableCell>
                  <TableCell className="text-right">{s.reservations}</TableCell>
                  <TableCell className="text-right">{s.closing}</TableCell>
                  <TableCell className="text-right">{s.conversion}%</TableCell>
                  <TableCell className="text-right">{fmtIDR(s.value)}</TableCell>
                  <TableCell className="text-right">{fmtIDR(s.avg)}</TableCell>
                </TableRow>
              )}
              {(!data?.by_branch || data.by_branch.length === 0) && (
                <TableRow><TableCell colSpan={6} className="text-center text-sm text-muted-foreground py-8">{loading ? "Memuat..." : "Belum ada data."}</TableCell></TableRow>
              )}
            </TableBody>
          </Table>
        </CardContent>
      </Card>
    </div>
  );
}
